import DOMPurify from 'dompurify';
import { useState } from 'react';
import { ArrowRight, Tick, Sparkles } from '@/components/icons';
import ChartRenderer from '@/components/preview/ChartRenderer';
import {
    REPORT_CONTENT_CLASSNAME,
    REPORT_CONTENT_WRAPPER_CLASSNAME,
} from '@/components/preview/constants';
import TestTableEditor from '@/components/preview/test-table-editor/TestTableEditor';
import { AppDialogLayout } from '@/components/system';
import { Button } from '@/components/ui/button';
import { ReportMeasurementProvider } from '@/context/ReportMeasurementContext';
import { resolveChartSpec } from '@/lib/chart-config';
import { isClassificationColoringOn } from '@/lib/classification-color';
import { keepOnlyFontSizeStyles } from '@/lib/rich-text';
import { resolveTableHeaderColor } from '@/lib/table-utils';
import { ReportData } from '@/types/report';
import { PendingConsolidation } from '@/types/report-payload';
import { WizardReviewStepFooter } from './WizardReviewStepFooter';

const ALLOWED_RICH_TEXT_TAGS = [
    'p',
    'br',
    'strong',
    'b',
    'em',
    'i',
    's',
    'u',
    'h1',
    'h2',
    'ul',
    'ol',
    'li',
    'blockquote',
    // Carries the font-size mark; see keepOnlyFontSizeStyles.
    'span',
];

const hasHtmlTag = (value: string) => /<\/?[a-z][\w:-]*(?:\s[^>]*)?>/i.test(value);

const sanitizeRichText = (value: string) =>
    keepOnlyFontSizeStyles(
        DOMPurify.sanitize(value, {
            ALLOWED_TAGS: ALLOWED_RICH_TEXT_TAGS,
            ALLOWED_ATTR: ['style'],
        }),
    );

function SafeRichText({ content }: { content?: string }) {
    if (!content?.trim()) return null;

    if (!hasHtmlTag(content)) {
        return (
            <div className={`${REPORT_CONTENT_WRAPPER_CLASSNAME} whitespace-pre-wrap`}>
                {content}
            </div>
        );
    }

    return (
        <div className={REPORT_CONTENT_WRAPPER_CLASSNAME}>
            <div
                className={REPORT_CONTENT_CLASSNAME}
                dangerouslySetInnerHTML={{
                    __html: sanitizeRichText(content),
                }}
            />
        </div>
    );
}

interface ConsolidationReviewModalProps {
    consolidations: PendingConsolidation[];
    reportData: ReportData;
    onAccept: (consolidation: PendingConsolidation) => void;
    onClose: () => void;
    onDismissPermanently?: () => void;
    skipIntro?: boolean;
    onBackToMenu?: () => void;
    onCompleted?: () => void;
}

function DismissCheckbox({
    checked,
    onChange,
}: {
    checked: boolean;
    onChange: (checked: boolean) => void;
}) {
    return (
        <label className="flex cursor-pointer select-none items-center gap-2">
            <input
                type="checkbox"
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
                className="size-3.5 rounded border-border-strong text-blue-600 focus:ring-blue-500"
            />
            <span className="text-xs text-muted-foreground">Não mostrar novamente</span>
        </label>
    );
}

export function ConsolidationReviewModal({
    consolidations,
    reportData,
    onAccept,
    onClose,
    onDismissPermanently,
    skipIntro = false,
    onBackToMenu,
    onCompleted,
}: ConsolidationReviewModalProps) {
    const [showIntro, setShowIntro] = useState(!skipIntro);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [acceptedIds, setAcceptedIds] = useState<Set<string>>(new Set());
    const [dismissChecked, setDismissChecked] = useState(false);

    const total = consolidations.length;
    const current = consolidations[currentIndex];

    const handleClose = () => {
        if (dismissChecked && onDismissPermanently) {
            onDismissPermanently();
        }
        onClose();
    };

    const handleCompleted = () => {
        if (onCompleted) {
            onCompleted();
            return;
        }

        handleClose();
    };

    // --- Intro splash page ---
    if (showIntro) {
        return (
            <AppDialogLayout
                footer={
                    <div className="flex items-center justify-between">
                        <div>
                            {onDismissPermanently && (
                                <DismissCheckbox
                                    checked={dismissChecked}
                                    onChange={setDismissChecked}
                                />
                            )}
                        </div>
                        <Button variant="outline" onClick={handleClose}>
                            Fechar
                        </Button>
                    </div>
                }
            >
                <div className="flex flex-col items-center justify-center px-6 py-10 text-center">
                    <div className="relative mb-6">
                        <div className="absolute -inset-3 animate-gradient-shift rounded-full bg-gradient-to-r from-[#0a2a8a] via-[#2e6fe8] to-[#62e3ff] bg-[length:200%_200%] opacity-20 blur-xl" />
                        <div className="relative animate-gradient-shift rounded-full bg-gradient-to-r from-[#0a2a8a] via-[#2e6fe8] to-[#62e3ff] bg-[length:200%_200%] p-5 shadow-lg shadow-blue-500/25">
                            <Sparkles className="size-8 text-white" />
                        </div>
                    </div>

                    <h2 className="max-w-sm text-xl font-bold leading-snug text-foreground">
                        Enquanto você estava fora, nossa IA corrigiu{' '}
                        <span className="text-blue-600">
                            {total} {total === 1 ? 'instrumento' : 'instrumentos'}
                        </span>{' '}
                        para você
                    </h2>

                    <p className="mt-3 max-w-xs text-sm text-muted-foreground">
                        Revise os resultados e adicione ao seu laudo com um clique.
                    </p>

                    <Button size="lg" className="mt-8 gap-2" onClick={() => setShowIntro(false)}>
                        Revisar resultados
                        <ArrowRight className="size-4" />
                    </Button>
                </div>
            </AppDialogLayout>
        );
    }

    // --- Completed state ---
    if (!current) {
        return (
            <AppDialogLayout
                footer={
                    <div className="flex justify-end">
                        <Button onClick={handleClose}>Fechar</Button>
                    </div>
                }
            >
                <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="mb-4 rounded-full bg-green-100 p-3">
                        <Tick className="size-6 text-green-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground">
                        Todos os resultados foram revisados
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Os resultados aceitos já foram adicionados ao seu laudo.
                    </p>
                </div>
            </AppDialogLayout>
        );
    }

    // --- Review carousel ---
    const isAccepted = acceptedIds.has(current.id);
    const blocks = current.payload?.blocks || [];
    const primaryColor = reportData.customizations?.colors?.primary || 'bg-brand-blue';
    const headerColor = resolveTableHeaderColor(
        reportData.customizations?.table?.header_color,
        reportData.customizations?.colors?.primary,
    );
    const textAlignment = reportData.customizations?.table?.text_alignment || 'center';
    const colorClassifications = isClassificationColoringOn(reportData.customizations?.table);

    const handleAccept = () => {
        onAccept(current);
        setAcceptedIds((prev) => new Set(prev).add(current.id));

        if (currentIndex < total - 1) {
            setCurrentIndex((i) => i + 1);
        } else {
            handleCompleted();
        }
    };

    const handleSkip = () => {
        if (currentIndex < total - 1) {
            setCurrentIndex((i) => i + 1);
        } else {
            handleCompleted();
        }
    };

    return (
        <AppDialogLayout
            title="Revise o resultado do instrumento"
            backAction={
                onBackToMenu
                    ? {
                          label: 'Voltar às opções',
                          onClick: onBackToMenu,
                      }
                    : undefined
            }
            closeAction={{
                label: 'Fechar',
                onClick: handleClose,
            }}
            footer={
                <WizardReviewStepFooter
                    total={total}
                    currentIndex={currentIndex}
                    onPrevious={() => setCurrentIndex((i) => Math.max(0, i - 1))}
                    onNext={() => setCurrentIndex((i) => Math.min(total - 1, i + 1))}
                    onSelect={setCurrentIndex}
                    getStepState={(index) =>
                        acceptedIds.has(consolidations[index]?.id) ? 'done' : 'pending'
                    }
                >
                    <Button variant="outline" onClick={handleSkip}>
                        Pular
                    </Button>
                    <Button onClick={handleAccept} disabled={isAccepted}>
                        {isAccepted ? (
                            <>
                                <Tick className="size-4" />
                                Aceito
                            </>
                        ) : (
                            'Aceitar resultado'
                        )}
                    </Button>
                </WizardReviewStepFooter>
            }
        >
            <ReportMeasurementProvider>
                <div className="force-light space-y-6 rounded-xl bg-card p-5">
                    {blocks.map((block, index) => (
                        <div key={block.id || index}>
                            {block.block_type === 'table' && (
                                <div className="space-y-3">
                                    {block.description && (
                                        <div className="relative border-l-2 border-border pl-4">
                                            <SafeRichText content={block.description} />
                                        </div>
                                    )}
                                    {block.rows && block.rows.length > 0 && (
                                        <TestTableEditor
                                            block={block}
                                            onUpdate={() => {}}
                                            primaryColor={primaryColor}
                                            headerColor={headerColor}
                                            textAlignment={textAlignment}
                                            colorClassifications={colorClassifications}
                                            disabled
                                        />
                                    )}
                                </div>
                            )}

                            {block.block_type === 'chart' && (
                                <div className="h-[250px]">
                                    <ChartRenderer
                                        spec={resolveChartSpec(block, { primaryColor })}
                                    />
                                </div>
                            )}

                            {block.block_type === 'text' && block.content && (
                                <SafeRichText content={block.content} />
                            )}
                        </div>
                    ))}
                </div>
            </ReportMeasurementProvider>
        </AppDialogLayout>
    );
}
